import {Component, OnDestroy, OnInit, PLATFORM_ID, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {defaultIfEmpty} from 'rxjs/operators';
import {TranslationService} from '../../../core/services/translation.service';
import {ConfigService} from '../../../core/services/config.service';
import {BaseDataService} from '../../../core/services/base-data.service';
import {LoginService} from '../../../core/components/login/login.service';
import {NavigationCommand, NavigationService} from '../../../data/services/navigation.service';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {BaseData} from '../../../data/models/base-data.model';
import {MatCard, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {ApplyIfNotEmptyDirective} from '../../../core/directives/apply-if-not-empty.directive';
import {LoadingSpinnerComponent} from '../../../core/components/loading-spinner/loading-spinner.component';
import {GeneralErrorsComponent} from '../../../core/components/general-errors/general-errors.component';

/**
 * Bestätigungsseite der Zubuchung für ein einzelnes Abonnement (P5W-4467).
 *
 * Route: /additional-booking/:orderId - `orderId` ist die BackendId (subId) des Hauptabonnements,
 * zu dem ein Kombi-Abo Print/EPaper gebucht werden soll.
 */
@Component({
  selector: 'app-additional-booking-confirm',
  templateUrl: './additional-booking-confirm.component.html',
  styleUrls: ['./additional-booking-confirm.component.scss'],
  imports: [
    ApplyIfNotEmptyDirective,
    LoadingSpinnerComponent,
    GeneralErrorsComponent,
    MatCard,
    MatCardTitle,
    MatCardSubtitle,
    MatButton
  ],
  standalone: true
})
export class AdditionalBookingConfirmComponent implements OnInit, OnDestroy {

  /** Werbeschlüssel, über den das zubuchbare Kombi-Produkt ermittelt wird. */
  public static readonly CAMPAIGN_CONFIG_KEY = 'subscription.printToKombiPrintEpaperCampaignId';

  protected orderId: string = null;
  /** Das über den Werbeschlüssel konfigurierte Produkt, das gebucht wird. */
  protected product: BaseData = null;
  protected errors: string[] = [];
  protected isLoading: boolean = true;
  /** Ergebnis der Qualifikationsprüfung. Erst danach wird überhaupt ein Produkt geladen. */
  protected isQualified: boolean = false;
  /** Läuft der Bestätigungs-Request? Solange bleibt der Button gesperrt. */
  protected isBooking: boolean = false;

  private paramSub: Subscription;

  public translationService = inject(TranslationService);
  private route = inject(ActivatedRoute);
  private configService = inject(ConfigService);
  private baseDataService = inject(BaseDataService);
  private loginService = inject(LoginService);
  private navigationService = inject(NavigationService);
  private subscriptionService = inject(HupSubscriptionsService);
  private platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Beim Server-Rendering liegt kein SSO-Token vor; Qualifikationsprüfung und Produktabruf
      // würden fehlschlagen und einen irreführenden Zustand rendern.
      return;
    }
    this.paramSub = this.route.paramMap.subscribe(params => {
      this.orderId = params.get('orderId');
      this.checkQualification();
    });
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
  }

  /** Prüft zuerst, ob das Abo überhaupt zubuchbar ist - erst danach wird das Produkt geladen. */
  private checkQualification(): void {
    this.isLoading = true;
    this.isQualified = false;
    this.product = null;
    this.errors = [];

    this.subscriptionService.checkSubscriptionIsExpandable(this.orderId).subscribe(qualified => {
      this.isQualified = qualified;
      if (!qualified) {
        this.errors = [this.translationService.getTranslation('additionalBooking_notQualified')];
        this.isLoading = false;
        return;
      }
      this.loadProduct();
    });
  }

  /**
   * Lädt über den konfigurierten Werbeschlüssel das zubuchbare Produkt.
   *
   * Der Key wird vom ConfigResolverService der Route vorgeladen (siehe app-routing.module.ts),
   * daher genügt hier der synchrone getConfig-Zugriff.
   */
  private loadProduct(): void {
    const campaignId = this.configService
      .getConfig(AdditionalBookingConfirmComponent.CAMPAIGN_CONFIG_KEY)?.value;
    if (!campaignId) {
      console.error('Kein Werbeschlüssel konfiguriert: '
        + AdditionalBookingConfirmComponent.CAMPAIGN_CONFIG_KEY);
      this.handleProductError();
      return;
    }
    this.loadProductForCampaign(campaignId);
  }

  private loadProductForCampaign(campaignId: string): void {
    // getProductByCampaignId schluckt Fehler und liefert dann EMPTY - ohne defaultIfEmpty käme
    // weder next noch error, und die Seite bliebe dauerhaft im Ladezustand stehen.
    this.baseDataService.getProductByCampaignId(campaignId).pipe(defaultIfEmpty(null)).subscribe({
      next: product => {
        if (!product) {
          console.error(`Zubuchbares Produkt für Werbeschlüssel ${campaignId} konnte nicht geladen werden`);
          this.handleProductError();
          return;
        }
        this.product = product;
        this.isLoading = false;
      },
      error: error => {
        console.error(`Zubuchbares Produkt für Werbeschlüssel ${campaignId} konnte nicht geladen werden`, error);
        this.handleProductError();
      }
    });
  }

  private handleProductError(): void {
    this.errors.push(this.translationService.getTranslation('additionalBooking_error_product'));
    this.isLoading = false;
  }

  /** Nur mit geprüftem Abo und geladenem Produkt darf gebucht werden. */
  public get canConfirm(): boolean {
    return this.isQualified && !!this.product && !this.isBooking;
  }

  public confirmBooking(): void {
    if (!this.canConfirm) {
      return;
    }
    this.isBooking = true;
    this.errors = [];

    this.loginService.getUserData().subscribe({
      next: userData => this.createCombiSubscription(userData.userBackendId),
      error: error => {
        console.error('Kundendaten für die Zubuchung konnten nicht geladen werden', error);
        this.errors.push(this.translationService.getTranslation('additionalBooking_error_confirm'));
        this.isBooking = false;
      }
    });
  }

  private createCombiSubscription(userBackendId: number): void {
    this.subscriptionService.createCombiSubscription(userBackendId, Number(this.orderId)).subscribe({
      next: createdSubscription => {
        this.isBooking = false;
        // Kombi-Abos werden direkt in Paradise angelegt; erst eine backendId > 0 belegt, dass
        // wirklich ein Abo entstanden ist. Ohne die wäre die Erfolgsmeldung eine Lüge.
        if (!createdSubscription?.backendId || createdSubscription.backendId <= 0) {
          console.error(`Zubuchung zu Abonnement ${this.orderId} lieferte kein angelegtes Abonnement`,
            createdSubscription);
          this.errors.push(this.translationService.getTranslation('additionalBooking_error_confirm'));
          return;
        }
        // replaceUrl: nach der Zubuchung ist diese Bestätigungsseite verbraucht - sie könnte für
        // dasselbe Abonnement nur noch "nicht mehr zubuchbar" melden. Der History-Eintrag wird
        // daher ersetzt, damit der Browser-Zurück-Weg von der Bestellbestätigung direkt auf die
        // Übersicht führt statt in diese Sackgasse.
        this.navigationService.navigateTo(
          NavigationCommand.ADDITIONALBOOKINGORDERED, [String(createdSubscription.backendId)], false, true);
      },
      error: error => {
        console.error(`Zubuchung für Abonnement ${this.orderId} fehlgeschlagen`, error);
        this.errors.push(this.translationService.getTranslation('additionalBooking_error_confirm'));
        this.isBooking = false;
      }
    });
  }

  /** Zurück zur Zubucher-Übersicht, von der aus die Karte angeklickt wurde. */
  public goBack(): void {
    this.navigationService.navigateTo(NavigationCommand.ADDITIONALBOOKINGOVERVIEW);
  }
}
